import { useEffect, useMemo, useRef, useState } from "react";
import { Dropdown } from "react-bootstrap";
import { BsCalendar3, BsChevronDown } from "react-icons/bs";
import { useSelector } from "react-redux";
import styled, { keyframes } from "styled-components";
import { media } from "../../helpers/responsive";
import { RootState } from "../../state/store";
import { formatDateString } from "../../helpers/utils";
import DatePresetSection from "./datePresetSection";
import { useFilterContext } from "../../helpers/hooks/useFilterContext";
import DateTimePickerSection from "./dateTimePickerSection";
import { format, parse } from "date-fns";
import DateRangePickerFooter from "./footerSection";
import PlanLimitNotice from "./planLimitNotice";
import {
  getEarliestAllowedDate,
  isExportRangeExceedsPlanLimit,
  isStartDateBeforePlanLimit,
  isViewRangeExceedsPlanLimit,
  LITE_MAX_EXPORT_DAYS,
  LITE_MAX_LOOKBACK_DAYS,
  LITE_MAX_SELECTION_DAYS,
  showDateRangeLimitUpgradeDialog,
  showExportRangeLimitDialog,
  showViewRangeLimitDialog,
  type DateRangePlanLimitMode,
} from "../../helpers/dateRangePlanLimits";

type DateRangePickerProps = {
  freeze?: boolean;
  planLimitMode?: DateRangePlanLimitMode;
};

const DateRangePicker = ({ freeze = false, planLimitMode = "view" }: DateRangePickerProps) => {
  const isExportMode = planLimitMode === "export";

  const [startDate, setStartDate] = useState<Date>(new Date());
  const [endDate, setEndDate] = useState<Date>(new Date());

  const [startHour, setStartHour] = useState(0);
  const [startMinute, setStartMinute] = useState(0);

  const [endHour, setEndHour] = useState(0);
  const [endMinute, setEndMinute] = useState(0);

  const {
    dates: selectedDates,
    locations: selectedLocations,
    timeRange: selectedTimeRanges,
  } = useSelector((state: RootState) => state.filters);

  const {
    plan_type: planType,
    max_lookback_days: maxLookbackDays,
    max_selection_days: maxSelectionDays,
    time_zone_offset: timezoneOffset,
  } = useSelector((state: RootState) => state.company.company_details);

  const planMinDate = useMemo(
    () => (isExportMode ? undefined : getEarliestAllowedDate(maxLookbackDays || LITE_MAX_LOOKBACK_DAYS, timezoneOffset || '+00:00')),
    [isExportMode, maxLookbackDays, timezoneOffset]
  );

  const liteExportDayLimit = LITE_MAX_EXPORT_DAYS;

  const liteSelectionDayLimit = maxSelectionDays || LITE_MAX_SELECTION_DAYS;

  const validateSelectedRange = (
    rangeStartDate: Date,
    rangeEndDate: Date,
    rangeStartTime: [number, number],
    rangeEndTime: [number, number]
  ) => {
    if (isExportMode) {
      if (planType === "lite" && isExportRangeExceedsPlanLimit(
        rangeStartDate,
        rangeEndDate,
        rangeStartTime,
        rangeEndTime,
        liteExportDayLimit
      )) {
        showExportRangeLimitDialog({
          maxExportDays: liteExportDayLimit,
          planType: planType || "lite",
        });
        return false;
      }
      return true;
    }

    if (isStartDateBeforePlanLimit(rangeStartDate, maxLookbackDays || LITE_MAX_LOOKBACK_DAYS, timezoneOffset || '+00:00')) {
      showDateRangeLimitPopup();
      return false;
    }

    if (
      planType === "lite" &&
      isViewRangeExceedsPlanLimit(
        rangeStartDate,
        rangeEndDate,
        rangeStartTime,
        rangeEndTime,
        liteSelectionDayLimit
      )
    ) {
      showViewRangeLimitDialog({
        maxSelectionDays: liteSelectionDayLimit,
        planType: planType || "lite",
        planMinDate,
      });
      return false;
    }

    return true;
  };

  const { applyFilter } = useFilterContext();

  const dropdownButtonRef = useRef(null);

  useEffect(() => {
    setStartDate(parse(selectedDates[0], "yyyy-MM-dd", new Date()));
    setEndDate(parse(selectedDates[1], "yyyy-MM-dd", new Date()));
  }, [selectedDates]);

  useEffect(() => {
    setStartHour(selectedTimeRanges[0][0]);
    setStartMinute(selectedTimeRanges[0][1]);
    setEndHour(selectedTimeRanges[1][0]);
    setEndMinute(selectedTimeRanges[1][1]);
  }, [selectedTimeRanges]);

  const padNumberWithZero = (value: number) => {
    return value.toString().padStart(2, "0");
  };

  const showDateRangeLimitPopup = () => {
    showDateRangeLimitUpgradeDialog({
      maxLookbackDays: maxLookbackDays || LITE_MAX_LOOKBACK_DAYS,
      planType: planType || "lite",
      planMinDate,
      maxSelectionDays: liteSelectionDayLimit,
    });
  };

  const applySelectedFilter = (dateRange, timeRange) => {
    const presetStartDate = parse(dateRange[0], "yyyy-MM-dd", new Date());
    const presetEndDate = parse(dateRange[1], "yyyy-MM-dd", new Date());

    if (!validateSelectedRange(
      presetStartDate,
      presetEndDate,
      timeRange[0] as [number, number],
      timeRange[1] as [number, number]
    )) {
      return;
    }

    applyFilter({
      type: "date",
      data: { dateRange, selectedLocations, timeRange },
    });
    toggleDropdownState();
  };

  const dateRangeText = useMemo(() => {
    let text = "";
    const range = {
      date: {
        start: selectedDates[0],
        end: selectedDates[1],
      },
      time: {
        start: {
          hour: padNumberWithZero(selectedTimeRanges[0][0]),
          minute: padNumberWithZero(selectedTimeRanges[0][1]),
        },
        end: {
          hour: padNumberWithZero(selectedTimeRanges[1][0]),
          minute: padNumberWithZero(selectedTimeRanges[1][1]),
        },
      },
    };
    const startTime = `${range.time.start.hour}:${range.time.start.minute}`;
    const endTime = `${range.time.end.hour}:${range.time.start.minute}`;
    const startDate = formatDateString(range.date.start);
    const endDate = formatDateString(range.date.end);
    if (range.date.start === range.date.end) {
      if (startTime == endTime) {
        text = `${startDate} - ${startTime}`;
      } else {
        text = `${startDate} - ${startTime} - ${endTime}`;
      }
    } else {
      text = `${startDate} - ${startTime} - ${endDate} - ${endTime}`;
    }
    return text;
  }, [selectedDates, selectedTimeRanges]);

  const toggleDropdownState = () => {
    if (dropdownButtonRef.current) {
      dropdownButtonRef.current.click();
    }
  };

  const onHideReset = (isOpen) => {
    if (!isOpen) {
      toggleDropdownState();
    }
  };

  const handleOnSubmit = () => {
    if (!validateSelectedRange(
      startDate,
      endDate,
      [startHour, startMinute],
      [endHour, endMinute]
    )) {
      return;
    }

    let dateRange = [
      format(startDate, "yyyy-MM-dd"),
      format(endDate, "yyyy-MM-dd"),
    ];
    let timeRange = [
      [startHour, startMinute],
      [endHour, endMinute],
    ];
    applySelectedFilter(dateRange, timeRange);
  };

  const handleOnReset = () => {
    setStartDate(parse(selectedDates[0], "yyyy-MM-dd", new Date()));
    setEndDate(parse(selectedDates[1], "yyyy-MM-dd", new Date()));
    toggleDropdownState();
  };

  const disableApplyButton = useMemo(() => {
    const dateCompare = [
      format(startDate, "yyyy-MM-dd"),
      format(endDate, "yyyy-MM-dd"),
    ];
    const timeCompare = [
      [startHour, startMinute],
      [endHour, endMinute],
    ];
    if (
      dateCompare[0] == selectedDates[0] &&
      dateCompare[1] == selectedDates[1] &&
      JSON.stringify(timeCompare[0]) == JSON.stringify(selectedTimeRanges[0]) &&
      JSON.stringify(timeCompare[1]) == JSON.stringify(selectedTimeRanges[1])
    ) {
      return true;
    }

    if (dateCompare[0] === dateCompare[1]) {
      // Get start and end time arrays where each array contains [hours, minutes]
      const startTime = timeCompare[0];
      const endTime = timeCompare[1];

      // First compare hours - if start hour is greater than end hour, return true
      if (startTime[0] > endTime[0]) {
        return true;
      } 
      // If hours are equal, compare minutes - if start minute is greater than end minute, return true
      else if (startTime[0] === endTime[0] && startTime[1] > endTime[1]) {
        return true;
      }
    }
    
    return false;
  }, [
    selectedDates,
    startDate,
    endDate,
    startHour,
    startMinute,
    endHour,
    endMinute,
  ]);

  return (
    <Wrapper className="date-range-select-control">
      <Dropdown onToggle={onHideReset}>
        <Dropdown.Toggle
          as={"button"}
          className="date-range-select-toggle"
          ref={dropdownButtonRef}
          disabled={freeze}>
          <BsCalendar3
            style={{ color: "#2B7BC5", fontSize: "24px", margin: "0 5px" }}
          />
          <span>{dateRangeText}</span>
          <BsChevronDown style={{ fontSize: "20px", margin: "0 5px" }} />
        </Dropdown.Toggle>
        <Dropdown.Menu className="date-range-select-dropdown">
          <DatePresetSection applyFilters={applySelectedFilter} />
          {planType === "lite" ? (
            <PlanLimitNotice
              maxLookbackDays={isExportMode ? liteExportDayLimit : (maxLookbackDays || LITE_MAX_LOOKBACK_DAYS)}
              maxSelectionDays={isExportMode ? undefined : liteSelectionDayLimit}
              planMinDate={planMinDate}
              mode={planLimitMode}
            />
          ) : null}
          <DateTimePickerSection
            startDate={startDate}
            endDate={endDate}
            setStartDate={setStartDate}
            setEndDate={setEndDate}
            startHour={startHour}
            setStartHour={setStartHour}
            startMinute={startMinute}
            setStartMinute={setStartMinute}
            endHour={endHour}
            setEndHour={setEndHour}
            endMinute={endMinute}
            setEndMinute={setEndMinute}
            planMinDate={isExportMode ? undefined : planMinDate}
            onPlanLimitInteract={isExportMode ? undefined : showDateRangeLimitPopup}
          />
          <DateRangePickerFooter
            onSubmit={handleOnSubmit}
            onReset={handleOnReset}
            disableSubmit={disableApplyButton}
          />
        </Dropdown.Menu>
      </Dropdown>
    </Wrapper>
  );
};

export default DateRangePicker;

const fadeInTop = keyframes`
  0% {
    -webkit-transform: translateY(-1px);
            transform: translateY(-1px);
    opacity: 0;
  }
  100% {
    -webkit-transform: translateY(-50);
            transform: translateY(-50);
    opacity: 1;
  }
`;

const Wrapper = styled.div`
  .dropdown-toggle::after {
    content: none;
  }
  .date-range-select-toggle {
    color: ${({ theme }) => theme.text};
    background-color: ${({ theme }) => theme.widget.background};
    &:disabled {
      color: ${({ theme }) => theme.dropdown.disabled.color};
      background-color: ${({ theme }) => theme.dropdown.disabled.background};
    }
    border-radius: 8px;
    font-size: 18px;
    font-weight: 500;
    border: 1px solid transparent;
    min-width: 208px;
    height: 44px;
    transition: border-color 0.25s linear 0s;
    &::after {
      content: none;
    }
  }
  .date-range-select-dropdown {
    animation: ${fadeInTop} 0.2s;
    min-width: 650px;
    padding: 20px;
    border: 1px solid ${({ theme }) => theme.border_color};
    border-radius: 20px;
    box-shadow: 5px 5px 30px rgba(0, 0, 0, 0.2);
  }
  ${media.down.lg} {
    .date-range-select-toggle {
      font-size: 14px;
      height: 40px;
    }
  }

  ${media.down.xs} {
    width: 100%;
    .date-range-select-toggle {
      width: 100%;
      min-width: auto;
      margin-left: 0;
      height: 40px;
      padding: 0 12px;
      font-size: 13px;
      display: flex;
      align-items: center;
      > svg {
        flex-shrink: 0;
        margin: 0 !important;
        &:first-child { width: 20px !important; height: 20px !important; }
        &:last-child { width: 14px !important; height: 14px !important; }
      }
      > span {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        flex: 1;
        min-width: 0;
        margin: 0 8px;
        text-align: center;
      }
    }
    .date-range-select-dropdown {
      min-width: auto;
      max-width: calc(100vw - 32px);
      width: calc(100vw - 32px);
    }
  }
`;
